import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, Instagram, Globe, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Replace 'YOUR_PAPERFORM_ID' with your actual Paperform form ID
// e.g., if your form URL is https://yourname.paperform.co/myform
// then PAPERFORM_ID = 'myform'
const PAPERFORM_ID = 'YOUR_PAPERFORM_ID';

const PaperformEmbed = () => {
  useEffect(() => {
    if (PAPERFORM_ID === 'YOUR_PAPERFORM_ID') return;
    const existing = document.querySelector('script[src*="paperform"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://paperform.co/__embed.min.js';
      script.async = true;
      document.body.appendChild(script);
      return () => {
        if (document.body.contains(script)) document.body.removeChild(script);
      };
    }
  }, []);

  if (PAPERFORM_ID === 'YOUR_PAPERFORM_ID') {
    return (
      <div
        data-testid="paperform-placeholder"
        className="flex flex-col items-center justify-center min-h-64 border-2 border-dashed border-white/20 text-white/50 text-center p-8"
      >
        <div className="w-12 h-12 border-2 border-white/20 mb-4" style={{ transform: 'rotate(45deg)' }} />
        <p className="font-poppins font-bold text-base text-white/70 mb-2">Paperform Embed</p>
        <p className="font-source-sans text-sm leading-relaxed">
          Abre <code className="text-[#4EBFBF]">src/pages/Contact.jsx</code> y reemplaza{' '}
          <code className="text-[#F2785C]">YOUR_PAPERFORM_ID</code> con el ID de tu formulario de Paperform.
        </p>
      </div>
    );
  }

  return (
    <div
      data-paperform-id={PAPERFORM_ID}
      data-testid="paperform-embed"
      style={{ minHeight: '400px' }}
    />
  );
};

const Contact = () => {
  const { t } = useTranslation();

  const contactInfo = [
    { icon: Mail, label: 'mikel@mikel.media', href: 'mailto:mikel@mikel.media', color: '#F2785C' },
    { icon: Phone, label: '+1 787 422 2734', href: 'tel:+17874222734', color: '#4EBFBF' },
    { icon: Instagram, label: '@mikelmedia', href: 'https://instagram.com/mikelmedia', color: '#7D468C' },
    { icon: Globe, label: 'www.mikelmedia.com', href: 'https://mikelmedia.com', color: '#4EBFBF' },
    { icon: MapPin, label: 'Puerto Rico & USA', href: null, color: '#F2785C' },
  ];

  return (
    <>
      <Navbar />
      <div
        data-testid="contact-page"
        className="min-h-screen bg-[#233559] dark:bg-[#0F1C2E] pt-16"
      >
        {/* Decorative diamonds bg */}
        <div className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-[#7D468C]/10 pointer-events-none" style={{ transform: 'rotate(45deg)', transformOrigin: 'top right' }} />
          <div className="absolute bottom-0 left-0 w-48 h-48 border-2 border-[#4EBFBF]/10 pointer-events-none" style={{ transform: 'rotate(45deg)', transformOrigin: 'bottom left' }} />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left: Info */}
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-3 h-3 bg-[#F2785C]" style={{ transform: 'rotate(45deg)' }} />
                  <span className="text-sm font-semibold text-[#4EBFBF] tracking-widest uppercase font-poppins">
                    {t('contact.subtitle')}
                  </span>
                </div>
                <h1 className="font-poppins font-black text-5xl text-white mb-4">
                  {t('contact.title')}
                </h1>
                <p className="text-white/65 text-lg mb-12 font-source-sans leading-relaxed">
                  {t('contact.description')}
                </p>

                <div className="space-y-5">
                  {contactInfo.map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-4">
                        <div
                          className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: item.color + '20', transform: 'rotate(45deg)' }}
                        >
                          <Icon size={14} style={{ color: item.color, transform: 'rotate(-45deg)' }} />
                        </div>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="text-white/75 hover:text-white transition-colors font-source-sans"
                            target={item.href.startsWith('http') ? '_blank' : undefined}
                            rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          >
                            {item.label}
                          </a>
                        ) : (
                          <span className="text-white/75 font-source-sans">{item.label}</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Diamond accent row */}
                <div className="flex gap-3 mt-14">
                  <div className="w-8 h-8 bg-[#7D468C]/40" style={{ transform: 'rotate(45deg)' }} />
                  <div className="w-8 h-8 border-2 border-[#4EBFBF]/40" style={{ transform: 'rotate(45deg)' }} />
                  <div className="w-8 h-8 bg-[#F2785C]/40" style={{ transform: 'rotate(45deg)' }} />
                </div>
              </div>

              {/* Right: Paperform embed */}
              <div className="bg-white/5 border border-white/10 p-6 sm:p-8">
                <PaperformEmbed />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Contact;
